import { regionForCountry } from './organizationRegion.js';
import { hasSignedMsa } from './msaSigning.js';

export const HQ_CLUSTER_ID = 'hq';
export const HQ_ADDITIONAL_CAPACITY = 4;
export const PAID_CLUSTER_CAPACITY = 5;
export const FOOTPRINT_REGIONS = ['North America', 'South America', 'Europe', 'Middle East', 'Africa', 'Asia', 'Oceania'] as const;

export type FootprintCluster = { id: string; region: string };
export type FootprintLocation = { country?: string; clusterId?: string; [key: string]: unknown };

export function areOrganizationLocationsLocked(organization: { isTestOrganization?: boolean; msaDocument?: unknown } | null | undefined): boolean {
    return organization?.isTestOrganization !== true && hasSignedMsa(organization);
}

export function arrangeMsaFootprint(hqCountry: string, inputLocations: FootprintLocation[], inputClusters: FootprintCluster[] = []) {
    const hqRegion = regionForCountry(hqCountry);
    const clusters = inputClusters.filter(cluster => cluster && typeof cluster.id === 'string' && typeof cluster.region === 'string')
        .map(cluster => ({ id: cluster.id.trim(), region: cluster.region.trim() }));
    const locations = inputLocations.map(location => ({ ...location }));
    const counts = new Map<string, number>([[HQ_CLUSTER_ID, 0]]);
    for (const cluster of clusters)
        counts.set(cluster.id, 0);
    for (const location of locations) {
        const region = regionForCountry(String(location.country || ''));
        let id = String(location.clusterId || '');
        const assignedRegion = id === HQ_CLUSTER_ID ? hqRegion : clusters.find(cluster => cluster.id === id)?.region;
        if (!id || !assignedRegion) {
            if (region === hqRegion && (counts.get(HQ_CLUSTER_ID) || 0) < HQ_ADDITIONAL_CAPACITY)
                id = HQ_CLUSTER_ID;
            else {
                const available = clusters.find(cluster => cluster.region === region && (counts.get(cluster.id) || 0) < PAID_CLUSTER_CAPACITY);
                if (available)
                    id = available.id;
                else {
                    let suffix = clusters.length + 1;
                    while (clusters.some(cluster => cluster.id === `cluster-${suffix}`))
                        suffix++;
                    id = `cluster-${suffix}`;
                    clusters.push({ id, region });
                    counts.set(id, 0);
                }
            }
        }
        location.clusterId = id;
        counts.set(id, (counts.get(id) || 0) + 1);
    }
    return { hqRegion, clusters, locations };
}

export function msaFootprintError(hqCountry: string, locations: FootprintLocation[], clusters: FootprintCluster[]): string {
    const hqRegion = regionForCountry(hqCountry);
    if (hqRegion === 'Global')
        return 'The HQ country has no supported regional cluster.';
    const ids = new Set<string>();
    for (const cluster of clusters) {
        if (!cluster.id || cluster.id === HQ_CLUSTER_ID || ids.has(cluster.id) || !FOOTPRINT_REGIONS.includes(cluster.region as typeof FOOTPRINT_REGIONS[number]))
            return 'Select a valid region for each additional cluster.';
        ids.add(cluster.id);
    }
    const counts = new Map<string, number>();
    for (const location of locations) {
        const cluster = clusters.find(item => item.id === location.clusterId);
        const region = location.clusterId === HQ_CLUSTER_ID ? hqRegion : cluster?.region;
        if (!region || regionForCountry(String(location.country || '')) !== region)
            return 'Each location must be in its selected cluster region.';
        counts.set(location.clusterId || '', (counts.get(location.clusterId || '') || 0) + 1);
    }
    if ((counts.get(HQ_CLUSTER_ID) || 0) > HQ_ADDITIONAL_CAPACITY)
        return 'The included HQ cluster allows four additional locations.';
    if (clusters.some(cluster => !counts.get(cluster.id) || (counts.get(cluster.id) || 0) > PAID_CLUSTER_CAPACITY))
        return 'Each additional cluster needs one to five locations.';
    return '';
}
